<template>
  <div class="mt-admin">
    <div class="mta-page-head">
      <h1>Metrics</h1>
      <span v-if="ready" class="mta-badge mta-badge--suc">{{ rangeLabel }}</span>
      <span class="mta-spacer"></span>
      <span v-if="refreshedAt" class="mta-muted" style="font-size:12px">Data as of {{ refreshedAt }} UTC</span>
      <button type="button" class="mta-btn mta-btn--secondary mta-btn--sm" :disabled="refreshing" @click="refresh">
        {{ refreshing ? 'Refreshing…' : 'Refresh now' }}
      </button>
      <button v-if="ready" type="button" class="mta-btn mta-btn--secondary mta-btn--sm" @click="exportAll">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16"/></svg>
        Export all (CSV)
      </button>
    </div>
    <p class="mta-page-sub">Usage, users and revenue for every POP product in one place. Click a card to expand its breakdown.</p>

    <div v-if="error" class="mta-error">{{ error }}</div>
    <div v-if="refreshError" class="mta-error">{{ refreshError }}</div>
    <div v-if="loading" class="mta-empty">Loading…</div>

    <div v-else-if="empty" class="mta-notice">
      No metrics have been computed yet. Click "Refresh now" to compute them (a few minutes), or run
      <code>manage.py refresh-admin-metrics</code>.
    </div>

    <template v-else-if="ready">
      <div class="mta-controls">
        <div class="mta-seg">
          <button v-for="preset in presets" :key="preset.label" type="button"
            :class="{ 'mta-active': preset.s === range.s && preset.e === range.e }" @click="setRange(preset.s, preset.e)">{{ preset.label }}</button>
        </div>
        <span class="mta-muted" style="font-size:12px">or custom:</span>
        <select class="mta-sel" :value="range.s" @change="setRange(+$event.target.value, range.e)">
          <option v-for="(m, i) in months" :key="'s' + m" :value="i">{{ monthLabel(m) }}</option>
        </select>
        <span class="mta-muted">to</span>
        <select class="mta-sel" :value="range.e" @change="setRange(range.s, +$event.target.value)">
          <option v-for="(m, i) in months" :key="'e' + m" :value="i">{{ monthLabel(m) }}</option>
        </select>
      </div>
      <p class="mta-cmp-note">Comparing vs previous period and vs same period last year.</p>

      <div class="mta-kpis">
        <div v-for="kpi in kpis" :key="kpi.id" class="mta-kpi">
          <div class="mta-eyebrow">{{ kpi.label }}</div>
          <div class="mta-kpi__v">{{ kpi.value }}</div>
          <div class="mta-kpi__d">
            <delta-chip :chip="kpi.prevChip" />
            <delta-chip :chip="kpi.yearChip" />
          </div>
        </div>
      </div>

      <section v-for="section in sections" :id="'mta-sec-' + section.id" :key="section.id" :ref="'sec-' + section.id">
        <div class="mta-sec-head"><h2 class="mta-h2">{{ section.name }}</h2></div>
        <div class="mta-mgrid">
          <template v-for="product in section.products">
            <metric-card
              :key="product.id"
              :product="product"
              :headline="headline(product)"
              :series="data.series[product.head]"
              :end-index="months.length - 1"
              :range-label="rangeLabel"
              :open="expanded === product.id"
              @toggle="toggle(product.id)" />
            <metric-detail
              v-if="expanded === product.id"
              :key="product.id + '-detail'"
              :product="product"
              :data="data"
              :defs="defs"
              :s="range.s"
              :e="range.e"
              :range-label="rangeLabel"
              :cm-filter="cmFilter[product.id] || null"
              :funnel="product.funnel ? data.funnel : null"
              @cm-filter="setCmFilter(product.id, $event)"
              @export="exportProduct(product)" />
          </template>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import DeltaChip from './DeltaChip.vue'
import MetricCard from './MetricCard.vue'
import MetricDetail from './MetricDetail.vue'
import { KPIS, METRICS, SECTIONS } from './metricsDefinitions'
import { calc, csvSlug, deltaChip, downloadCsv, fmt, monthLabel, productCsvRows, rangeLabel, yearChip } from './metricsCalc'

const REFRESH_POLL_MS = 5000
const REFRESH_POLL_MAX = 240  // 20 minutes

/**
 * Admin > Metrics (design "POP Admin - Usage Metrics.html"): the Overview / Usage / Users / Revenue tabs
 * are this one page; `section` scrolls to the tab's section. The numbers come from the stored refresh
 * (GET /api/admin/metrics/series/); "Refresh now" recomputes them in the background.
 */
export default {
  name: 'AdminMetricsOverview',
  components: { DeltaChip, MetricCard, MetricDetail },
  props: {
    section: { type: String, default: '' },
  },
  data () {
    return {
      loading: false,
      error: '',
      refreshError: '',
      data: null,
      refreshing: false,
      pollTimer: null,
      range: { s: 0, e: 0 },
      expanded: null,
      cmFilter: {},
      defs: METRICS,
      sections: SECTIONS,
    }
  },
  computed: {
    ready () {
      return Boolean(this.data && this.data.months && this.data.months.length)
    },
    empty () {
      return Boolean(this.data && this.data.status === 'EMPTY')
    },
    months () {
      return this.ready ? this.data.months : []
    },
    refreshedAt () {
      return this.data && this.data.refreshedAt
    },
    presets () {
      const last = this.months.length - 1
      if (last < 0) return []
      const clamp = (i) => Math.max(0, i)
      return [
        { label: 'This month', s: last, e: last },
        { label: 'Last month', s: clamp(last - 1), e: clamp(last - 1) },
        { label: '3 mo', s: clamp(last - 2), e: last },
        { label: '6 mo', s: clamp(last - 5), e: last },
        { label: '12 mo', s: clamp(last - 11), e: last },
      ]
    },
    rangeLabel () {
      return rangeLabel(this.months, this.range.s, this.range.e)
    },
    kpis () {
      return KPIS.map((kpi) => {
        const c = calc(this.data, this.defs, this.defs[kpi.id], this.range.s, this.range.e)
        return { id: kpi.id, label: kpi.label, value: fmt(c.cur, c.def.fmt), prevChip: deltaChip(c, 'prev'), yearChip: yearChip(c, 'yr') }
      })
    },
  },
  watch: {
    section () {
      this.scrollToSection()
    },
  },
  created () {
    this.load()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    monthLabel,
    load () {
      this.loading = true
      this.error = ''
      return this.$store.dispatch('getAdminMetricsSeries')
        .then((response) => {
          const data = response && response.data
          if (!data || (data.status !== 'SUCCESS' && data.status !== 'EMPTY')) throw new Error('bad response')
          const first = !this.data
          this.data = data
          if (data.refresh && data.refresh.state === 'running' && !data.refresh.stale) this.startPolling()
          if (first && this.ready) {
            const last = this.months.length - 1
            this.range = { s: Math.max(0, last - 1), e: Math.max(0, last - 1) }
            this.$nextTick(() => this.scrollToSection())
          }
        })
        .catch(() => {
          this.error = 'We could not load the metrics. Try again in a moment.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    setRange (s, e) {
      if (s > e) [s, e] = [e, s]
      this.range = { s, e }
    },
    toggle (productId) {
      this.expanded = this.expanded === productId ? null : productId
    },
    setCmFilter (productId, key) {
      this.$set(this.cmFilter, productId, key)
    },
    headline (product) {
      return calc(this.data, this.defs, this.defs[product.head], this.range.s, this.range.e)
    },
    scrollToSection () {
      if (!this.section || !this.ready) return
      const el = document.getElementById('mta-sec-' + this.section)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    exportProduct (product) {
      downloadCsv('pop-' + product.id + '-' + csvSlug(this.rangeLabel) + '.csv',
        productCsvRows(this.data, this.defs, product, this.range.s, this.range.e))
    },
    exportAll () {
      let rows = []
      this.sections.forEach((section) => {
        section.products.forEach((product) => {
          rows.push([section.name + ' · ' + product.name])
          rows = rows.concat(productCsvRows(this.data, this.defs, product, this.range.s, this.range.e))
          rows.push([])
        })
      })
      downloadCsv('pop-metrics-all-' + csvSlug(this.rangeLabel) + '.csv', rows)
    },
    refresh () {
      this.refreshError = ''
      this.refreshing = true
      return this.$store.dispatch('refreshAdminMetrics')
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.refreshError = (data && data.msg) || 'The refresh could not be started.'
            this.refreshing = false
            return
          }
          this.startPolling()
        })
        .catch(() => {
          this.refreshError = 'The refresh could not be started.'
          this.refreshing = false
        })
    },
    startPolling () {
      this.refreshing = true
      this.stopPolling()
      const before = this.refreshedAt
      let ticks = 0
      this.pollTimer = setInterval(() => {
        ticks += 1
        this.$store.dispatch('getAdminMetricsSeries').then((response) => {
          const data = response && response.data
          if (!data) return
          const state = data.refresh && data.refresh.state
          const finished = data.refreshedAt && data.refreshedAt !== before
          if (finished || state === 'failed' || (state !== 'running' && ticks > 2)) {
            this.stopPolling()
            this.refreshing = false
            if (state === 'failed') this.refreshError = 'The refresh failed: ' + ((data.refresh && data.refresh.error) || 'unknown error')
            if (data.status === 'SUCCESS') this.data = data
          }
        }).catch(() => {})
        if (ticks >= REFRESH_POLL_MAX) {
          this.stopPolling()
          this.refreshing = false
        }
      }, REFRESH_POLL_MS)
    },
    stopPolling () {
      if (this.pollTimer) clearInterval(this.pollTimer)
      this.pollTimer = null
    },
  },
}
</script>

<style lang="scss">
@import './metricsAdmin';
</style>
